import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Button, Card, Input, Select, Table, Modal } from './common';
import { useSidebar } from '../context/SidebarContext';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const { isCollapsed } = useSidebar();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role_id: '',
    department: '',
    region: '',
    unit: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      // The endpoint returns { data, pagination }; passing the envelope
      // straight through makes Table receive an object and throw
      // "data.map is not a function". fetchRoles below already unwraps it.
      setUsers(response.data?.data || []);
    } catch (err) {
      setError('Failed to fetch users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/roles', { params: { limit: 1000 } });
      setRoles(response.data.data);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        // Update user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if empty
        }
        await api.put(`/users/${editingUser.id}`, updateData);
        setSuccess('User updated successfully');
      } else {
        // Create user
        await api.post('/users', formData);
        setSuccess('User created successfully');
      }
      
      fetchUsers();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role_id: user.role_id || '',
      department: user.department || '',
      region: user.region || '',
      unit: user.unit || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await api.delete(`/users/${userId}`);
      setSuccess('User deleted successfully');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role_id: '',
      department: '',
      region: '',
      unit: ''
    });
    setError('');
  };

  const handleAddNew = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role_id: '',
      department: '',
      region: '',
      unit: ''
    });
    setShowModal(true);
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { 
      key: 'role_name', 
      label: 'Role',
      render: (user) => user.role_name || user.role || '-'
    },
    { key: 'department', label: 'Department' },
    { key: 'region', label: 'Region' },
    { key: 'unit', label: 'Unit' },
    {
      key: 'actions',
      label: 'Actions',
      render: (user) => (
        <div className="action-buttons">
          <Button 
            variant="secondary" 
            size="small"
            onClick={() => handleEdit(user)}
          >
            Edit
          </Button>
          <Button 
            variant="danger" 
            size="small"
            onClick={() => handleDelete(user.id)}
          >
            Delete
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="user-management">
      <div className="page-header">
        <h1>User Management</h1>
        <Button onClick={handleAddNew}>
          + Add User
        </Button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <Card compact={isCollapsed} allowOverflow={true}>
        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <Table 
            columns={columns}
            data={users}
            keyField="id"
          />
        )}
      </Card>

      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingUser ? 'Edit User' : 'Add New User'}
      >
        <form onSubmit={handleSubmit} className="user-form">
          <Input
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />

          <Input
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />

          <Input
            label={editingUser ? "Password (leave empty to keep current)" : "Password"}
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            required={!editingUser}
          />

          <Select
            label="Role"
            name="role_id"
            value={formData.role_id}
            onChange={handleInputChange}
            options={[
              { value: '', label: 'Select Role' },
              ...roles.map(role => ({
                value: role.id,
                label: role.role_name
              }))
            ]}
          />

          <Input
            label="Department"
            name="department"
            value={formData.department}
            onChange={handleInputChange}
          />

          <Input
            label="Region"
            name="region"
            value={formData.region}
            onChange={handleInputChange}
          />

          <Input
            label="Unit"
            name="unit"
            value={formData.unit}
            onChange={handleInputChange}
          />

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit">
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UserManagement;
